import type { IOrganizationRiskSeverityThreshold } from '@/types/organization'
import { RISK_STATUS } from '@/lib/constants/status'

export const RISK_TREATMENT_OPTIONS = {
  MITIGATE: 'mitigate' as const,
  ACCEPT: 'accept' as const,
} as const

export const RISK_LEVELS = {
  VERY_LOW: 'veryLow',
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  VERY_HIGH: 'veryHigh',
  UNKNOWN: 'unknown',
}

export const RISK_LEVEL_ORDER = {
  [RISK_LEVELS.VERY_LOW]: 1,
  [RISK_LEVELS.LOW]: 2,
  [RISK_LEVELS.MEDIUM]: 3,
  [RISK_LEVELS.HIGH]: 4,
  [RISK_LEVELS.VERY_HIGH]: 5,
  [RISK_LEVELS.UNKNOWN]: 6,
}

export const DEFAULT_RISK_THRESHOLDS: IOrganizationRiskSeverityThreshold[] = [
  {
    level: RISK_LEVELS.VERY_LOW,
    max: 5,
  },
  {
    level: RISK_LEVELS.LOW,
    max: 10,
  },
  {
    level: RISK_LEVELS.MEDIUM,
    max: 15,
  },
  {
    level: RISK_LEVELS.HIGH,
    max: 20,
  },
  {
    level: RISK_LEVELS.VERY_HIGH,
    max: 25,
  },
]

export const MAX_RISK_EFFECTIVENESS = 5
// Likelihood and impact both go from 1 to 5.
export const MAX_RISK_SCORE = 5
export const MIN_RISK_EFFECTIVENESS = 0
// Shared by the blank risk form and catalogue risks: a new risk starts at medium until someone assesses it.
export const NEW_RISK_DEFAULTS = {
  status: RISK_STATUS.PLANNED,
  treatmentType: RISK_TREATMENT_OPTIONS.MITIGATE,
  effectiveness: MIN_RISK_EFFECTIVENESS,
  levels: {
    inherentLikelihood: 3,
    inherentImpact: 4,
    residualLikelihood: 3,
    residualImpact: 4,
  },
}
export const RISK_DISPLAY_ID_PREFIX = 'R' as const
export const RISK_METHODOLOGY_STORAGE_KEY = 'risk-methodology'
export const RISK_METHODOLOGY = {
  SIMPLE: 'simple',
  ADVANCED: 'advanced',
} as const
export type IRiskMethodology = (typeof RISK_METHODOLOGY)[keyof typeof RISK_METHODOLOGY]

export const RISK_CHANGE_ACTION = {
  CREATED: 'created' as const,
  UPDATED: 'updated' as const,
  DELETED: 'deleted' as const,
} as const

export const RISK_CHANGE_FIELD = {
  DISPLAY_ID: 'displayId' as const,
  NAME: 'name' as const,
  STATUS: 'status' as const,
  OWNER: 'owner' as const,
  INHERENT_LIKELIHOOD: 'inherentLikelihood' as const,
  INHERENT_IMPACT: 'inherentImpact' as const,
  INHERENT_RISK_LEVEL: 'inherentRiskLevel' as const,
  RESIDUAL_LIKELIHOOD: 'residualLikelihood' as const,
  RESIDUAL_IMPACT: 'residualImpact' as const,
  RESIDUAL_RISK_LEVEL: 'residualRiskLevel' as const,
  EFFECTIVENESS: 'effectiveness' as const,
  TREATMENT_TYPE: 'treatmentType' as const,
  TREATMENT_NOTES: 'treatmentNotes' as const,
  CONTROLS: 'controls' as const,
  CLUSTERS: 'clusters' as const,
  ASSETS: 'assets' as const,
  AFFECTED_ASSETS: 'affectedAssets' as const,
  THREATS: 'threats' as const,
  VULNERABILITIES: 'vulnerabilities' as const,
  IMPACT_DIMENSIONS: 'impactDimensions' as const,
  INTERESTED_PARTIES: 'interestedParties' as const,
  REACH: 'reach' as const,
  NOTES: 'notes' as const,
  CREATED_AT: 'createdAt' as const,
  CLOSED_DATE: 'closedDate' as const,
} as const

export const RISK_ASSETS = {
  SERVERS_IT_PRODUCTION: 'servers_it_production' as const,
  SERVERS_INTERNAL_DEVELOPMENT: 'servers_internal_development' as const,
  SOURCE_CODE: 'source_code' as const,
  WEB_APPLICATIONS_EXTERNAL_PRODUCTION: 'web_applications_external_production' as const,
  PHYSICAL_SERVERS: 'physical_servers' as const,
  LAPTOPS_WINDOWS_LINUX_MAC: 'laptops_windows_linux_mac' as const,
  BYOD: 'byod' as const,
  CORPORATE_MOBILES: 'corporate_mobiles' as const,
  CORPORATE_TABLETS: 'corporate_tablets' as const,
  USB_DRIVES: 'usb_drives' as const,
  USER_PERIPHERALS: 'user_peripherals' as const,
  PRINTERS: 'printers' as const,
  OFFICES_BARCELONA: 'offices_barcelona' as const,
  WIRELESS_INFRASTRUCTURE: 'wireless_infrastructure' as const,
  SUBCONTRACTED_OFFICE_FACILITIES: 'subcontracted_office_facilities' as const,
  COMPANY_OWNED_OFFICE_FACILITIES: 'company_owned_office_facilities' as const,
  SAAS_CORE: 'saas_core' as const,
  SAAS_NON_CORE: 'saas_non_core' as const,
  PUBLIC_INFORMATION: 'public_information' as const,
  CONFIDENTIAL_INFORMATION_TECHNICAL_ORGANIZATIONAL: 'confidential_information_technical_organizational' as const,
  CONFIDENTIAL_INFORMATION_PERSONAL_PII_PHI: 'confidential_information_personal_pii_phi' as const,
  CONFIDENTIAL_INFORMATION_CORE: 'confidential_information_core' as const,
  TOP_MANAGEMENT: 'top_management' as const,
  EMPLOYEES: 'employees' as const,
  FREELANCERS: 'freelancers' as const,
  ADMINISTRATORS: 'administrators' as const,
} as const

export const RISK_INTERESTED_PARTIES = {
  EMPLOYEES: 'employees' as const,
  SYSTEMS_ADMINISTRATORS: 'systems_administrators' as const,
  FREELANCERS: 'freelancers' as const,
  CLIENTS: 'clients' as const,
  PROSPECTS: 'prospects' as const,
  SUPPLIERS: 'suppliers' as const,
  SHAREHOLDERS: 'shareholders' as const,
  REGULATORY_AUTHORITIES: 'regulatory_authorities' as const,
  GOVERNMENT: 'government' as const,
  GOVERNMENT_AGENCIES_CYBERSECURITY: 'government_agencies_cybersecurity' as const,
  SOCIETY: 'society' as const,
  COMPETITORS: 'competitors' as const,
  BUSINESS_PARTNERS: 'business_partners' as const,
} as const
